import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/common/Button';
import { LookupFieldSelect } from '../../components/formFields/LookupFieldSelect';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import {
  createCourt,
  COURT_NAME_MAX_LENGTH,
  getCourtById,
  listCourtMasterCourtTypes,
  listCourtMasterDivisions,
  updateCourt,
} from '../../services/courtMasters';
import type { LookupItem } from '../../services/criminalCaseRequests';
import { setFlashToast } from '../../utils/flashToast';
import {
  focusFirstInvalid,
  validateRequired,
  type RequiredErrors,
  type RequiredFieldSpec,
} from '../../utils/requiredValidation';
import { useAuth } from '../../contexts/AuthContext';
import { exceedsMax, maxCharLimitExceededError } from '../../utils/caseFieldValidation';

const READONLY_FIELD_STYLE = {
  background: 'color-mix(in srgb, var(--bg) 70%, var(--surface))',
  color: 'var(--t2)',
  cursor: 'not-allowed' as const,
};

export default function CourtFormPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { courtId } = useParams();
  const isEdit = Boolean(courtId);
  const nav = useNavigate();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [reqErrors, setReqErrors] = useState<RequiredErrors>({});

  const [courtTypes, setCourtTypes] = useState<LookupItem[]>([]);
  const [divisions, setDivisions] = useState<LookupItem[]>([]);
  const [lookupsState, setLookupsState] = useState<'idle' | 'loading' | 'ready'>('idle');

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [courtTypeId, setCourtTypeId] = useState('');
  const [divisionId, setDivisionId] = useState('');
  const [active, setActive] = useState(true);

  const clearReqError = (key: string) => {
    setReqErrors((cur) => {
      if (!cur[key]) return cur;
      const next = { ...cur };
      delete next[key];
      return next;
    });
  };

  useEffect(() => {
    if (!user) return;
    let alive = true;
    setLookupsState('loading');
    void Promise.all([listCourtMasterCourtTypes(), listCourtMasterDivisions()]).then(
      ([typesRes, divisionsRes]) => {
        if (!alive) return;
        setCourtTypes(typesRes.success ? typesRes.data : []);
        setDivisions(divisionsRes.success ? divisionsRes.data : []);
        setLookupsState('ready');
      },
    );
    return () => {
      alive = false;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!isEdit || !courtId) return;
    let alive = true;
    setLoading(true);
    void getCourtById(courtId).then((res) => {
      if (!alive) return;
      if (res.success) {
        const d = res.data;
        setName(d.name);
        setCode(d.code);
        setCourtTypeId(d.courtTypeId);
        setDivisionId(d.divisionId);
        setActive(d.active);
      } else {
        setReqErrors({ form: res.message ?? t('admin.shared.requestFailed') });
      }
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [courtId, isEdit, t]);

  const requiredSpecs = useMemo<RequiredFieldSpec[]>(() => {
    return [
      {
        key: 'name',
        label: t('metadata.courts.courtName'),
        value: name,
        elementId: 'court-name',
      },
      {
        key: 'courtTypeId',
        label: t('metadata.courts.courtType'),
        value: courtTypeId,
        elementId: 'court-type',
      },
      {
        key: 'divisionId',
        label: t('metadata.courts.division'),
        value: divisionId,
        elementId: 'court-division',
      },
    ];
  }, [courtTypeId, divisionId, name, t]);

  async function handleSave() {
    if (savingRef.current) return;

    const errs = validateRequired(requiredSpecs);
    if (exceedsMax(name, COURT_NAME_MAX_LENGTH)) {
      errs.name = maxCharLimitExceededError();
    }
    setReqErrors(errs);
    if (Object.keys(errs).length) {
      focusFirstInvalid(requiredSpecs, errs);
      return;
    }

    savingRef.current = true;
    setSaving(true);
    let keepLocked = false;
    try {
      const payload = {
        name: name.trim(),
        active,
        courtTypeId,
        divisionId,
      };
      const res =
        isEdit && courtId ? await updateCourt(courtId, payload) : await createCourt(payload);
      if (!res.success) {
        setReqErrors({ form: res.message ?? t('admin.shared.requestFailed') });
        return;
      }
      setFlashToast({
        message: isEdit ? t('metadata.courtForm.updateSuccess') : t('metadata.courtForm.createSuccess'),
        variant: 'success',
      });
      nav('/metadata/courts');
      keepLocked = true;
    } finally {
      if (!keepLocked) {
        savingRef.current = false;
        setSaving(false);
      }
    }
  }

  const lookupsLoading = lookupsState === 'loading';

  return (
    <>
      <div className="bc">
        <span>{t('nav.groups.metadata')}</span>
        <span className="bc-sep">/</span>
        <Link to="/metadata/courts">{t('metadata.courts.title')}</Link>
        <span className="bc-sep">/</span>
        {isEdit ? t('metadata.courtForm.updateTitle') : t('metadata.courtForm.addTitle')}
      </div>

      <div className="pg-title">{isEdit ? t('metadata.courtForm.updateTitle') : t('metadata.courtForm.addTitle')}</div>

      <div className="cr-table-wrap" style={{ overflow: 'visible' }}>
        <div style={{ padding: 18 }}>
          {loading ? <div style={{ color: 'var(--t2)' }}>{t('common.loading')}</div> : null}
          {reqErrors.form ? <div className="si-err" style={{ marginBottom: 12 }}>{reqErrors.form}</div> : null}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="al-f">
              <div className="al-l">
                {t('metadata.courts.courtName')} <span className="req">*</span>
              </div>
              <input
                id="court-name"
                className="al-inp"
                value={name}
                maxLength={COURT_NAME_MAX_LENGTH}
                placeholder={t('metadata.courtForm.namePlaceholder')}
                disabled={loading}
                style={reqErrors.name ? { borderColor: 'var(--red)' } : undefined}
                aria-invalid={reqErrors.name ? true : undefined}
                onChange={(e) => {
                  setName(e.target.value);
                  clearReqError('name');
                }}
              />
              {reqErrors.name ? (
                <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }}>{reqErrors.name}</div>
              ) : null}
            </div>

            {isEdit ? (
              <div className="al-f">
                <div className="al-l">{t('metadata.courts.courtCode')}</div>
                <input
                  className="al-inp"
                  value={code}
                  readOnly
                  disabled
                  style={READONLY_FIELD_STYLE}
                />
              </div>
            ) : null}

            <LookupFieldSelect
              id="court-type"
              label={t('metadata.courts.courtType')}
              required
              value={courtTypeId}
              placeholder={lookupsLoading ? t('common.loading') : t('metadata.courtForm.selectCourtType')}
              options={courtTypes.map((x) => ({ id: x.id, label: x.name }))}
              error={reqErrors.courtTypeId}
              disabled={lookupsLoading || loading}
              emptyMessage={t('common.noDataFound')}
              onChange={(v) => {
                setCourtTypeId(v);
                clearReqError('courtTypeId');
              }}
            />

            <LookupFieldSelect
              id="court-division"
              label={t('metadata.courts.division')}
              required
              value={divisionId}
              placeholder={lookupsLoading ? t('common.loading') : t('metadata.courtForm.selectDivision')}
              options={divisions.map((x) => ({ id: x.id, label: x.name }))}
              error={reqErrors.divisionId}
              disabled={lookupsLoading || loading}
              emptyMessage={t('common.noDataFound')}
              onChange={(v) => {
                setDivisionId(v);
                clearReqError('divisionId');
              }}
            />

            <div className="al-f" style={{ gridColumn: '1 / -1' }}>
              <label className="ccr-chk" style={{ marginBottom: 0 }}>
                <input
                  type="checkbox"
                  checked={active}
                  disabled={loading}
                  onChange={(e) => setActive(e.target.checked)}
                />
                <span>{t('common.active')}</span>
              </label>
            </div>
          </div>
        </div>

        <div className="tft" style={{ background: 'color-mix(in srgb, var(--bg) 60%, var(--surface))' }}>
          <div />
          <div className="pgr">
            <button className="mfb" type="button" disabled={saving} onClick={() => setCancelConfirmOpen(true)}>
              {t('common.cancel')}
            </button>
            <Button
              variant="primary"
              onClick={() => {
                void handleSave();
              }}
              disabled={saving || loading}
            >
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title={t('admin.shared.cancelChanges')}
        message={t('metadata.courtForm.cancelConfirm')}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => {
          setCancelConfirmOpen(false);
          nav('/metadata/courts');
        }}
      />
    </>
  );
}
